import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

// ═══════════════════════════════════════════════════════════════
// Context-aware filler detection — avoids false positives
// "right" in "that's right" is NOT a filler; "right, so..." IS
// ═══════════════════════════════════════════════════════════════
const FILLERS = {
  'um': { always: true },
  'umm': { always: true },
  'uh': { always: true },
  'uhh': { always: true },
  'like': { always: false, pattern: /\b(like,|like\s+um|like\s+uh|,\s*like\b|like\s+you\s+know)/gi },
  'basically': { always: true },
  'you know': { always: true },
  'sort of': { always: true },
  'kind of': { always: false, pattern: /\bkind of\s+(like|um|uh|you know)\b/gi },
  'literally': { always: true },
  'honestly': { always: true },
  'so yeah': { always: true },
  'and stuff': { always: true },
  'i mean': { always: true },
  // "right", "well", "actually" are EXCLUDED — too many false positives
};

const highlightFillers = (text) => {
  if (!text) return text;

  let result = text;
  const highlights = [];

  for (const [filler, config] of Object.entries(FILLERS)) {
    if (config.always) {
      const regex = new RegExp(`\\b${filler}\\b`, 'gi');
      let match;
      while ((match = regex.exec(text)) !== null) {
        highlights.push({ start: match.index, end: match.index + match[0].length, word: match[0] });
      }
    } else if (config.pattern) {
      let match;
      while ((match = config.pattern.exec(text)) !== null) {
        highlights.push({ start: match.index, end: match.index + match[0].length, word: match[0] });
      }
    }
  }

  if (highlights.length === 0) return null; // No fillers found, return null to signal clean text

  // Sort highlights by position and build highlighted JSX
  highlights.sort((a, b) => a.start - b.start);

  const parts = [];
  let lastIdx = 0;
  for (const h of highlights) {
    if (h.start > lastIdx) {
      parts.push({ text: text.slice(lastIdx, h.start), isFiller: false });
    }
    parts.push({ text: text.slice(h.start, h.end), isFiller: true });
    lastIdx = h.end;
  }
  if (lastIdx < text.length) {
    parts.push({ text: text.slice(lastIdx), isFiller: false });
  }

  return parts;
};

const TranscriptSection = ({ transcript, annotations, computedMetrics }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'user' | 'ai' | 'flagged'

  // Safely format time
  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '00:00';
    const val = typeof seconds === 'number' ? seconds : parseInt(seconds, 10);
    if (isNaN(val)) return '00:00';
    const mins = Math.floor(val / 60);
    const secs = Math.floor(val % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Build annotation index by TURN ID (not timestamp proximity)
  const annotationByTurnId = useMemo(() => {
    if (!annotations || !transcript) return {};
    const map = {};

    for (const ann of annotations) {
      // Strategy 1: Direct turn ID match (if LLM provided turnId field)
      if (ann.turnId) {
        map[ann.turnId] = ann;
        continue;
      }

      // Strategy 2: Map annotation timestamp to NEAREST transcript turn ID
      if (ann.timestamp && transcript.length > 0) {
        let bestIdx = 0;
        let bestDiff = Infinity;

        for (let i = 0; i < transcript.length; i++) {
          const turnTs = transcript[i].timestamp || 0;
          const diff = Math.abs(turnTs - ann.timestamp);
          if (diff < bestDiff) {
            bestDiff = diff;
            bestIdx = i;
          }
        }
        const turnId = transcript[bestIdx].id || bestIdx + 1;
        map[turnId] = ann;
      }
    }
    return map;
  }, [annotations, transcript]);

  if (!transcript || transcript.length === 0) return null;

  // Filter turns
  const filteredTranscript = transcript.filter(entry => {
    if (filter === 'all') return true;
    const isUser = entry.speaker === 'You' || entry.speaker === 'User' || entry.speaker === 'user';
    if (filter === 'user') return isUser;
    if (filter === 'ai') return !isUser;
    if (filter === 'flagged') {
      const turnId = entry.id || transcript.indexOf(entry) + 1;
      return !!annotationByTurnId[turnId];
    }
    return true;
  });

  // Show first 20 turns or all if expanded
  const visibleTranscript = isExpanded ? filteredTranscript : filteredTranscript.slice(0, 20);
  const hasMore = filteredTranscript.length > 20;

  return (
    <div id="session-transcript" className="mt-16 bg-white border border-slate-100 rounded-[3rem] p-8 shadow-sm relative overflow-hidden scroll-mt-24">
      <div className="absolute top-0 right-0 p-8 opacity-[0.02] pointer-events-none">
        <Icon name="MessageSquare" size={200} />
      </div>

      <div className="flex items-center justify-between pb-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="p-2.5 rounded-xl bg-slate-900 text-white shadow-lg shadow-slate-200">
            <Icon name="MessageSquare" size={20} />
          </div>
          <div className="space-y-0.5">
            <h3 className="text-sm font-semibold text-slate-900 tracking-wider">Session Transcript</h3>
            <p className="text-[10px] font-medium text-slate-400">
              {transcript.length} messages • Filler words highlighted in yellow
            </p>
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex items-center gap-1.5">
          {[
            { key: 'all', label: 'All', count: transcript.length },
            { key: 'user', label: 'You' },
            { key: 'ai', label: 'AI' },
            { key: 'flagged', label: 'Insights', icon: 'Search' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-2.5 py-1 rounded-lg text-[9px] font-medium tracking-widest transition-all ${
                filter === f.key
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
            >
              {f.icon && <Icon name={f.icon} size={9} className="mr-1 inline" />}
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2.5">
        {visibleTranscript.map((entry, idx) => {
          const isUser = entry.speaker === 'You' || entry.speaker === 'User' || entry.speaker === 'user';
          const turnId = entry.id || transcript.indexOf(entry) + 1;
          const annotation = annotationByTurnId[turnId];

          // Highlight fillers in user speech
          const fillerParts = isUser ? highlightFillers(entry.text) : null;

          // Sentiment border based on annotation
          const sentimentBorder = annotation
            ? annotation.sentiment === 'positive' ? 'border-l-4 border-l-emerald-400'
              : annotation.sentiment === 'negative' ? 'border-l-4 border-l-red-400'
              : 'border-l-4 border-l-amber-400'
            : '';

          return (
            <div key={idx} id={`transcript-turn-${turnId}`} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} space-y-1 transition-all duration-500 ${sentimentBorder} ${sentimentBorder ? 'pl-3 rounded-l-lg' : ''}`}>
              {/* Speaker Label & Time */}
              <div className={`flex items-center gap-2 px-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                <span className="text-[10px] font-normal text-slate-400 tracking-widest uppercase">
                  {isUser ? 'You' : 'Interviewer'}
                </span>
                <span className="text-[9px] font-normal text-slate-300 font-mono">
                  {formatTime(entry.timestamp)}
                </span>
              </div>

              {/* Chat Bubble */}
              <div className={`group relative max-w-[72%] flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
                <div className={`px-4 py-2.5 rounded-[1.5rem] text-sm font-normal leading-relaxed shadow-sm transition-all duration-300 ${
                  isUser
                  ? 'bg-emerald-50 text-emerald-900 border border-emerald-100 rounded-tr-none'
                  : 'bg-white border border-slate-100 text-slate-600 rounded-tl-none group-hover:border-slate-200'
                }`}>
                  {/* Render with filler highlights or plain text */}
                  {fillerParts ? (
                    fillerParts.map((part, pIdx) =>
                      part.isFiller ? (
                        <span key={pIdx} className="bg-amber-200/60 text-amber-900 rounded px-0.5 mx-0.5 font-normal" title="Filler word">
                          {part.text}
                        </span>
                      ) : (
                        <span key={pIdx}>{part.text}</span>
                      )
                    )
                  ) : (
                    entry.text
                  )}
                </div>

                {/* Inline AI Analysis */}
                {annotation && (
                  <div className={`mt-1.5 max-w-[90%] rounded-[1.2rem] p-3 flex gap-3 shadow-sm hover:shadow-md transition-all ${
                    isUser ? 'mr-2' : 'ml-2'
                  } ${
                    annotation.sentiment === 'positive' ? 'bg-emerald-50/50 border border-emerald-100/50'
                      : annotation.sentiment === 'negative' ? 'bg-red-50/50 border border-red-100/50'
                      : 'bg-amber-50/50 border border-amber-100/50'
                  }`}>
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border ${
                      annotation.sentiment === 'positive' ? 'bg-white text-emerald-500 border-emerald-100'
                        : annotation.sentiment === 'negative' ? 'bg-white text-red-500 border-red-100'
                        : 'bg-white text-amber-500 border-amber-100'
                    }`}>
                      <Icon name={
                        annotation.sentiment === 'positive' ? 'ThumbsUp'
                          : annotation.sentiment === 'negative' ? 'AlertTriangle'
                          : 'Search'
                      } size={12} />
                    </div>
                    <div>
                      <span className={`text-[8px] font-normal tracking-widest block mb-0.5 ${
                        annotation.sentiment === 'positive' ? 'text-emerald-500'
                          : annotation.sentiment === 'negative' ? 'text-red-500'
                          : 'text-amber-500'
                      }`}>
                        {annotation.sentiment === 'positive' ? 'STRENGTH' : annotation.sentiment === 'negative' ? 'AREA TO IMPROVE' : 'AI INSIGHT'}
                      </span>
                      <p className="text-[11px] text-slate-600 font-normal italic leading-snug">
                        "{annotation.comment || annotation.text}"
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Expand/Collapse */}
      {hasMore && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full mt-6 py-3 flex items-center justify-center gap-2 text-[10px] font-medium text-slate-400 tracking-widest hover:text-slate-600 transition-colors border-t border-slate-50"
        >
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
          {isExpanded ? 'Show Less' : `Show All ${filteredTranscript.length} Messages`}
        </button>
      )}
    </div>
  );
};

export default TranscriptSection;
